/**
 * Zustand store for the conversion state machine.
 *
 * The upload flow is modeled as a linear state machine:
 *   idle → uploading → processing → done
 *                                 ↘ error
 *
 * Keeping this in a flat Zustand store (rather than React Query mutations)
 * gives us direct, synchronous state transitions from XHR event callbacks.
 */

import { create } from "zustand";
import type { ConversionOptions, ConversionResult } from "../api/types";
import { DEFAULT_OPTIONS } from "../api/types";

export type UploadState =
  | "idle"
  | "uploading"
  | "processing"
  | "done"
  | "error";

export interface AppError {
  error_code: string;
  message: string;
}

interface ConversionStore {
  // Current session state
  selectedFile: File | null;
  options: ConversionOptions;
  uploadState: UploadState;
  uploadProgress: number;   // 0–100 during 'uploading'
  currentJobId: string | null;
  result: ConversionResult | null;
  error: AppError | null;

  // Actions
  setFile: (file: File | null) => void;
  setOptions: (patch: Partial<ConversionOptions>) => void;
  setUploading: (progress?: number) => void;
  setUploadProgress: (progress: number) => void;
  setProcessing: (jobId?: string) => void;
  setDone: (result: ConversionResult) => void;
  setError: (error: AppError) => void;
  reset: () => void;
}

const initialState = {
  selectedFile: null,
  options: DEFAULT_OPTIONS,
  uploadState: "idle" as UploadState,
  uploadProgress: 0,
  currentJobId: null,
  result: null,
  error: null,
};

export const useConversionStore = create<ConversionStore>((set) => ({
  ...initialState,

  setFile: (file) => set({ selectedFile: file, error: null }),

  setOptions: (patch) =>
    set((state) => ({ options: { ...state.options, ...patch } })),

  setUploading: (progress = 0) =>
    set({ uploadState: "uploading", uploadProgress: progress, error: null }),

  setUploadProgress: (progress) => set({ uploadProgress: progress }),

  setProcessing: (jobId) =>
    set({ uploadState: "processing", currentJobId: jobId ?? null }),

  setDone: (result) =>
    set({ uploadState: "done", result, error: null }),

  setError: (error) =>
    set({ uploadState: "error", error }),

  reset: () => set(initialState),
}));
